// src/layouts/SidebarLayout/Header/Buttons/Notifications/index.js
import {
  alpha,
  Badge,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useEffect, useRef, useState, useCallback } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { styled } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { socket, initializeSocket } from 'src/socket/config';
import { useTranslation } from 'react-i18next';
import {
  fetchAllNotification,
  markAsViewNotification,
  deleteNotification,
  deleteAllReadNotifications
} from 'src/redux/notificationSlice';
import { 
  playNotif, 
  requestNotificationPermission 
} from 'src/utils/sound';

// ---------- Badge ----------
const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    .MuiBadge-badge {
      background-color: ${alpha(theme.palette.error.main, 0.9)};
      color: white;
      min-width: 16px;
      height: 16px;
      padding: 0;
      &::after {
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 100%;
        border-radius: 50%;
        box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
        content: "";
      }
    }
`
);

// ---------- Anim cloche ----------
const BellButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  '&.ringing::after': {
    content: '""',
    position: 'absolute',
    inset: -6,
    borderRadius: '50%',
    boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.35)}`,
    animation: 'pulse 900ms ease-out 1'
  },
  '&.ringing svg': {
    animation: 'ring 900ms ease-in-out 1'
  },
  '@keyframes ring': {
    '0%': { transform: 'rotate(0)' },
    '15%': { transform: 'rotate(18deg)' },
    '30%': { transform: 'rotate(-14deg)' },
    '45%': { transform: 'rotate(10deg)' },
    '60%': { transform: 'rotate(-6deg)' },
    '75%': { transform: 'rotate(3deg)' },
    '100%': { transform: 'rotate(0)' }
  },
  '@keyframes pulse': {
    '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.35)}` },
    '100%': { boxShadow: `0 0 0 14px ${alpha(theme.palette.success.main, 0)}` }
  }
}));

// Récupère claimId quel que soit le shape
function getClaimIdFromNotif(notif) {
  if (!notif) return null;
  return (
    notif.claimId ||
    notif.claim ||
    notif.claim_id ||
    notif?.data?.claimId ||
    notif?.data?.claim ||
    notif?.meta?.claimId ||
    notif?.payload?.claimId ||
    notif?.action?.claimId ||
    notif?.action?.claim ||
    null
  );
}

function HeaderNotifications() {
    const { t, i18n } = useTranslation();

  const ref = useRef(null);
  const prevIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const [isOpen, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(null);
  const [ringing, setRinging] = useState(false);
  
  // ✅ État des notifications sonores (persisté dans localStorage)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved === null ? true : saved === 'true'; // Activé par défaut
  });

  const [notifPermission, setNotifPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const notifications = useSelector(
    (state) => state.notifications.notifications || []
  );
  const unreadCount = notifications.filter((n) => !n.view).length;

  const handleClose = () => setOpen(false);

  // ✅ Toggle son avec sauvegarde
  const handleToggleSound = async (event) => {
    const newValue = event.target.checked;
    
    // Si l'utilisateur active les sons pour la première fois
    if (newValue && notifPermission !== 'granted') {
      const granted = await requestNotificationPermission();
      if (granted) {
        setNotifPermission('granted');
        setSoundEnabled(true);
        localStorage.setItem('notificationSoundEnabled', 'true');
      } else {
        setSoundEnabled(false);
        localStorage.setItem('notificationSoundEnabled', 'false');
      }
    } else {
      setSoundEnabled(newValue);
      localStorage.setItem('notificationSoundEnabled', newValue.toString());
    }
  };

  const fetchNotifications = useCallback(
  debounce(async (silent = false) => {  // ✅ Ajout paramètre silent
    try {
      const res = await dispatch(fetchAllNotification()).unwrap();
      const currentIds = new Set(res.map((n) => n._id));
      const newNotifications = [];

      res.forEach((n) => {
        if (!prevIdsRef.current.has(n._id) && !n.view) {
          newNotifications.push(n);
        }
      });

      prevIdsRef.current = currentIds;

      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        return;
      }

      // ✅ Ne pas jouer le son si silent=true
      if (!silent && newNotifications.length > 0 && soundEnabled) {
        setRinging(true);
        setTimeout(() => setRinging(false), 1000);
        const lastNotif = newNotifications[0];
        const title = lastNotif.kind === 'message' ? '💬 Nouveau message' : '🔔 Nouvelle notification';
        playNotif(title, lastNotif.message || 'Vous avez une nouvelle notification');
      }
    } catch (error) {
      console.error(error);
    }
  }, 200),
  [dispatch, soundEnabled]
);
  // ✅ UN SEUL useEffect pour gérer le socket
  useEffect(() => {
    
    // Connexion initiale du socket
    initializeSocket();
    
    // Premier chargement des notifications
    fetchNotifications();

const handleNotifMessage = (payload) => {
  if (soundEnabled) {
    setRinging(true);
    setTimeout(() => setRinging(false), 1000);
    playNotif('💬 Nouveau message', payload.message?.message || 'Vous avez un nouveau message');
  }
  fetchNotifications(true); // ✅ silent=true → pas de 2ème son
};
    const handleNewMessage = (msg) => {
      fetchNotifications();
    };

    const handleSocketConnect = () => {
    };

    const handleSocketDisconnect = (reason) => {
    };

    // ✅ Écouter les événements
    socket.on('connect', handleSocketConnect);
    socket.on('disconnect', handleSocketDisconnect);
    socket.on('notif-message', handleNotifMessage);
    socket.on('claim:message:new', handleNewMessage);

    // ✅ Cleanup à la destruction du composant
    return () => {
      
      socket.off('connect', handleSocketConnect);
      socket.off('disconnect', handleSocketDisconnect);
      socket.off('notif-message', handleNotifMessage);
      socket.off('claim:message:new', handleNewMessage);
      
      // NE PAS déconnecter le socket ici car il est partagé globalement
    };
  }, [fetchNotifications]);

  const handleBellClick = () => {
    setOpen(true);
  };

  const handleNotificationClick = useCallback(
    async (notif) => {
      try {
        await dispatch(markAsViewNotification(notif._id)).unwrap();
        await dispatch(deleteAllReadNotifications()).unwrap();

        if (notif.kind === 'message') {
          const claimId = getClaimIdFromNotif(notif);
          navigate(
            claimId
              ? `/dashboards/messenger?claimId=${encodeURIComponent(claimId)}`
              : '/dashboards/messenger'
          );
        } else if (notif.kind === 'Claim') {
          navigate('/management/claims');
        }
      } catch (error) {
        // Naviguer quand même en cas d'erreur
        const claimId = getClaimIdFromNotif(notif);
        if (notif.kind === 'message') {
          navigate(
            claimId
              ? `/dashboards/messenger?claimId=${encodeURIComponent(claimId)}`
              : '/dashboards/messenger'
          );
        } else if (notif.kind === 'Claim') {
          navigate('/management/claims');
        }
      } finally {
        handleClose();
      }
    },
    [dispatch, navigate]
  );

  const handleDeleteNotification = useCallback(
    async (e, notif) => {
      e.stopPropagation();
      try {
        await dispatch(deleteNotification(notif._id)).unwrap();
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  return (
    <>
      <Tooltip arrow title={t('Notifications')}>
        <BellButton
          color="primary"
          ref={ref}
          onClick={handleBellClick}
          className={ringing ? 'ringing' : undefined}
        >
          <NotificationsBadge
            badgeContent={unreadCount}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {soundEnabled ? (
              <NotificationsActiveTwoToneIcon />
            ) : (
              <NotificationsOffIcon />
            )}
          </NotificationsBadge>
        </BellButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* ✅ Header avec toggle son */}
        <Box
          sx={{ p: 2 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">{t('Notifications')}</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={soundEnabled}
                onChange={handleToggleSound}
                size="small"
                color="success"
              />
            }
            label={
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                🔊 Son
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Box>
        
        <Divider />
        
        {/* ✅ Message d'information SEULEMENT si refusé */}
        {notifPermission === 'denied' && soundEnabled && (
          <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderBottom: '1px solid rgba(255, 152, 0, 0.3)' }}>
            <Typography variant="caption" color="warning.main">
              ⚠️ Les notifications sonores sont bloquées par votre navigateur.
              <br />
              Cliquez sur l'icône 🔒 dans la barre d'adresse pour les autoriser.
            </Typography>
          </Box>
        )}
        
        <List sx={{ p: 0 }}>
          {notifications.length > 0 ? (
            notifications.map((notif, index) => (
              <ListItem
                button
                onClick={() => handleNotificationClick(notif)}
                key={notif._id || index}
                sx={{
                  p: 2,
                  minWidth: 350,
                  display: { xs: 'block', sm: 'flex' },
                  backgroundColor: !notif.view
                    ? 'rgba(151, 181, 151, 0.138)'
                    : 'none'
                }}
              >
                <Box
                  flex="1"
                  onMouseEnter={() => setIsHovered(index)}
                  onMouseLeave={() => setIsHovered(null)}
                >
                  {isHovered === index && (
                    <Grid
                      container
                      justifyContent="flex-end"
                      marginTop={-1.5}
                      marginLeft={1.6}
                      marginBottom={-0.5}
                    >
                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={(e) => handleDeleteNotification(e, notif)}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Grid>
                  )}

                  <Box display="flex" justifyContent="space-between">
                    <Typography
                      sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
                    >
                      {notif?.kind || 'notification'}
                      {!notif?.view && (
                        <FiberManualRecordIcon
                          sx={{ fontSize: 12, ml: 0.5, color: '#18e68e' }}
                        />
                      )}
                    </Typography>
                    <Typography variant="caption" sx={{ textTransform: 'none' }}>
                      {formatDistance(
                        new Date(notif?.createdAt || Date.now()),
                        new Date(),
                        { addSuffix: true }
                      )}
                    </Typography>
                  </Box>

                  <Typography component="span" variant="body2" color="text.secondary">
                    {notif?.message}
                  </Typography>
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem
              sx={{ p: 2, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}
            >
              <Box flex="1">
                <Box display="flex" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 'bold' }}>
                    Aucune notification pour le moment.
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          )}
        </List>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
